import React, { useMemo } from "react";
import { DetailsBasePage } from "./DetailsBasePage.tsx";
import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { groupBy, kebabCase } from "lodash";

type RecommendationEntry = {
  name: string;
  city: string;
  neighborhood: "Westside" | "Central LA" | "Northeast LA" | "South Bay";
  category: string;
  subCategory?: string;
};

//@ts-ignore
const recommendations: RecommendationEntry[] = [
  {
    name: "Ondo",
    city: "Hollywood",
    neighborhood: "Central LA",
    category: "Coffee",
  },
  {
    name: "Community Goods",
    city: "Fairfax",
    neighborhood: "Central LA",
    category: "Coffee",
  },
  {
    name: "Alchemist",
    city: "Koreatown",
    neighborhood: "Central LA",
    category: "Coffee",
  },
  {
    name: "Yeems",
    city: "Koreatown",
    neighborhood: "Central LA",
    category: "Coffee",
  },
  {
    name: "Kumquat",
    city: "Highland Park",
    neighborhood: "Northeast LA",
    category: "Coffee",
  },
  {
    name: "Stereoscope",
    city: "Echo Park",
    neighborhood: "Central LA",
    category: "Coffee",
  },
  {
    name: "Thank You Coffee",
    city: "Chinatown",
    neighborhood: "Central LA",
    category: "Coffee",
  },
  {
    name: "Little Lunch",
    city: "Venice",
    neighborhood: "Westside",
    category: "Coffee",
  },
  {
    name: "Upside Down",
    city: "Westwood",
    neighborhood: "Westside",
    category: "Coffee",
  },
  {
    name: "Smoking Tiger",
    city: "Gardena",
    neighborhood: "South Bay",
    category: "Coffee",
  },
  {
    name: "Cofax",
    city: "Fairfax",
    neighborhood: "Central LA",
    category: "Breakfast",
    subCategory: "Breakfast Burritos",
  },
  {
    name: "Clark Street Diner",
    city: "Hollywood",
    neighborhood: "Central LA",
    category: "Breakfast",
    subCategory: "Diner",
  },
  {
    name: "Republique",
    city: "Fairfax",
    neighborhood: "Central LA",
    category: "Breakfast",
    subCategory: "French",
  },
  {
    name: "Sqirl",
    city: "Silver Lake",
    neighborhood: "Central LA",
    category: "Breakfast",
    subCategory: "Brunch",
  },
  {
    name: "Open Market",
    city: "Koreatown",
    neighborhood: "Central LA",
    category: "Lunch",
    subCategory: "Sandwiches",
  },
  {
    name: "Larchmont Wine & Cheese",
    city: "Larchmont",
    neighborhood: "Central LA",
    category: "Lunch",
    subCategory: "Sandwiches",
  },
  {
    name: "Burgers Never Say Die",
    city: "Silver Lake",
    neighborhood: "Central LA",
    category: "Lunch",
    subCategory: "Burgers",
  },
  {
    name: "Uchi",
    city: "West Hollywood",
    neighborhood: "Central LA",
    category: "Dinner",
    subCategory: "Sushi",
  },
  {
    name: "Sun Nong Dan",
    city: "Koreatown",
    neighborhood: "Central LA",
    category: "Dinner",
    subCategory: "Korean",
  },
  {
    name: "Kouraku",
    city: "Little Tokyo",
    neighborhood: "Central LA",
    category: "Dinner",
    subCategory: "Japanese",
  },
  {
    name: "Joy",
    city: "Highland Park",
    neighborhood: "Northeast LA",
    category: "Dinner",
    subCategory: "Taiwanese",
  },
  {
    name: "Osteria Mozza",
    city: "Hancock Park",
    neighborhood: "Central LA",
    category: "Dinner",
    subCategory: "Italian",
  },
  {
    name: "Shin Sen Gumi",
    city: "Gardena",
    neighborhood: "South Bay",
    category: "Dinner",
    subCategory: "Yakitori",
  },
  {
    name: "Kagura",
    city: "Torrance",
    neighborhood: "South Bay",
    category: "Dinner",
    subCategory: "Japanese",
  },
  {
    name: "King's Hawaiian Bakery",
    city: "Torrance",
    neighborhood: "South Bay",
    category: "Lunch",
    subCategory: "Hawaiian",
  },
  {
    name: "Amoeba",
    city: "Hollywood",
    neighborhood: "Central LA",
    category: "Record Stores",
  },
  {
    name: "Boogie Maru Sounds",
    city: "Torrance",
    neighborhood: "South Bay",
    category: "Record Stores",
  },
  {
    name: "High Fidelity Records",
    city: "Culver City",
    neighborhood: "Westside",
    category: "Record Stores",
  },
  {
    name: "Gimme Gimme Records",
    city: "Highland Park",
    neighborhood: "Northeast LA",
    category: "Record Stores",
  },
  {
    name: "Record Safari",
    city: "Los Feliz",
    neighborhood: "Central LA",
    category: "Record Stores",
  },
  {
    name: "We Share Records",
    city: "Sawtelle",
    neighborhood: "Westside",
    category: "Record Stores",
  },
  {
    name: "Darling",
    city: "West Hollywood",
    neighborhood: "Central LA",
    category: "Dinner",
    subCategory: "American",
  },
].sort((a, b) => a.city.localeCompare(b.city));

export const LaRecs = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [groupByKey, setGroupByKey] = React.useState("category");

  const groupedRecommendations = useMemo(() => {
    return groupBy(recommendations, (entry) => entry[groupByKey]) as Record<
      string,
      RecommendationEntry[]
    >;
  }, [groupByKey]);

  return (
    <DetailsBasePage
      title={isSmallScreen ? "LA Recs" : "Los Angeles Recommendations"}
    >
      <Typography variant="body2" component={"em"}>
        Official neighborhoods according to{" "}
        <Link href="https://maps.latimes.com/neighborhoods/">
          Los Angeles Times
        </Link>
      </Typography>
      <Stack
        useFlexGap
        spacing={2}
        sx={(theme) => ({
          mt: 2,
          spacing: 2,
          flexDirection: "row",
          alignItems: "center",
          [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
          },
        })}
      >
        <Typography>Organize by:</Typography>
        <Button
          sx={{
            minWidth: 150,
          }}
          size="small"
          variant={groupByKey === "category" ? "contained" : "outlined"}
          onClick={() => setGroupByKey("category")}
        >
          Category
        </Button>
        <Button
          sx={{
            minWidth: 150,
          }}
          size="small"
          variant={groupByKey === "neighborhood" ? "contained" : "outlined"}
          onClick={() => setGroupByKey("neighborhood")}
        >
          Neighborhood
        </Button>{" "}
      </Stack>
      <Box
        sx={(theme) => ({
          display: "grid",
          gap: 4,
          gridTemplateAreas:
            groupByKey === "category"
              ? `"coffee breakfast"  "coffee lunch" "record-stores dinner"`
              : `"central-la northeast-la" "central-la westside" "central-la south-bay"`,

          [theme.breakpoints.down("sm")]: {
            gridTemplateAreas: "unset",
            gridTemplateColumns: "1fr",
          },
        })}
      >
        {Object.entries(groupedRecommendations).map(([key, entries]) => {
          return (
            <Stack
              key={key}
              gridArea={isSmallScreen ? undefined : kebabCase(key)}
              spacing={2}
              mt={2}
            >
              <Typography variant="h5" fontWeight={"bold"}>
                {key}
              </Typography>
              <Stack spacing={1}>
                {entries.map((entry, idx) => {
                  //If there is no subcategory, use the category if grouped by neighborhood
                  let subCategory = entry.subCategory;
                  if (groupByKey === "neighborhood") {
                    subCategory ??= entry.category;
                  }

                  return (
                    <Stack key={idx}>
                      <Box
                        sx={(theme) => ({
                          display: "grid",
                          gridTemplateColumns: "max-content max-content",
                          gap: 2,
                          [theme.breakpoints.down("md")]: {
                            gridTemplateColumns: "1fr",
                            gap: 1,
                            mb: 1,
                          },
                        })}
                      >
                        <Typography fontWeight={"bold"}>
                          {entry.name}
                        </Typography>
                        <Typography fontStyle={"italic"}>
                          {entry.city} {!!subCategory && `- ${subCategory}`}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          );
        })}
      </Box>
    </DetailsBasePage>
  );
};

/*
LA Recommendations

Coffee 
- Ondo (North Hollywood)
- Community Goods (Fairfax)
- Alchemist (Koreatown)
- Yeems (Koreatown)
- Makisupa (Silver Lake)
- Mad Lab (Culver City)

Breakfast
- Cofax (Fairfax)
- Clark Street Diner (North Hollywood)
- Republique (Fairfax)


Lunch
- Open Market (Koreatown)
- Larchmont Wine & Cheese (Larchmont Village)
- Burgers Never Say Die (Silver Lake)
- LOQUI (Culver City)


Dinner
- Uchi (West Hollywood)
- Sun Nong Dan (Koreatown)
- Kouraku (Little Tokyo)
- Joy on York (Highland Park)
- Osteria Mozza (Hancock Park)

Record Stores
- Amoeba (Hollywood)
- Boogie Maru Sounds (Torrance)
- High Fidelity Records (Culver City)

*/
